import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { trackPlanView, trackPlanUpgradeStart, trackAiCreditPurchase } from "../lib/analytics.ts";
import {
  INTERVALS,
  PLAN_LIMITS,
  availableIntervals,
  defaultInterval,
  intervalLabel,
  isUnlimited,
  overageRateText,
  planHasSso,
  planMemberLimit,
  rateLimitText,
  SELECTABLE_PLANS,
  type OrgPlansResponse,
  type PlanInterval,
  type PlanRecord,
} from "@currencycore/shared";
import {
  createBillingCheckout,
  getAiCreditPackages,
  getOrgPlans,
  previewPlanChange,
  purchaseAiCredits,
  type AiCreditPackageView,
  type PlanChangePreview,
} from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";

const fmt = (n: number) => n.toLocaleString("en-US");
const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
const SHORT: Record<PlanInterval, string> = { weekly: "wk", monthly: "mo", quarterly: "qtr", half_yearly: "6mo", annual: "yr" };

/** Lowest non-zero price across all intervals, or null if the plan has none. */
function lowestPrice(p: PlanRecord): { cents: number; interval: PlanInterval } | null {
  let best: { cents: number; interval: PlanInterval } | null = null;
  for (const i of INTERVALS) {
    const c = p.prices[i] ?? 0;
    if (c > 0 && (best === null || c < best.cents)) best = { cents: c, interval: i };
  }
  return best;
}

function hasAnyPrice(p: PlanRecord): boolean {
  return INTERVALS.some((i) => (p.prices[i] ?? 0) > 0);
}

function CheckSVG() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: "14px", height: "14px", color: "var(--accent-text)", flexShrink: 0, marginTop: "2px" }}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function PlansPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<OrgPlansResponse | null>(null);
  const [selected, setSelected] = useState<PlanInterval | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A paid→paid change goes through this confirmation first (an immediate charge
  // or a scheduled downgrade is consequential, so it shouldn't be one click).
  const [confirm, setConfirm] = useState<{ plan: PlanRecord; isDowngrade: boolean } | null>(null);
  // Proration preview for an upgrade confirm: "loading" while Dodo is queried.
  const [preview, setPreview] = useState<PlanChangePreview | "loading" | null>(null);
  // AI credit add-ons (one-time purchases).
  const [packages, setPackages] = useState<AiCreditPackageView[] | null>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);
  const aiCreditsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (orgId) getOrgPlans(orgId).then((d) => { setData(d); trackPlanView(d.current.kind); }).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (orgId) getAiCreditPackages(orgId).then((r) => setPackages(r.packages)).catch(() => setPackages([]));
  }, [orgId]);

  // Scroll to the AI credits section when navigated here via #ai-credits
  // (e.g. the "Purchase credits" buttons on the Usage page). Waits until the
  // page data + packages have rendered the section, then scrolls next frame.
  useEffect(() => {
    if (location.hash === "#ai-credits" && data && packages !== null) {
      const id = requestAnimationFrame(() =>
        aiCreditsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
      return () => cancelAnimationFrame(id);
    }
  }, [location.hash, data, packages]);

  async function buyCredits(packageId: string) {
    if (!orgId) return;
    setBuying(packageId);
    setBuyError(null);
    const pkg = packages?.find((p) => p.id === packageId);
    if (pkg) trackAiCreditPurchase(packageId, pkg.credits);
    try {
      const { url } = await purchaseAiCredits(orgId, packageId);
      if (url) window.location.href = url;
      else setBuyError("Checkout isn't available in this environment.");
    } catch (err) {
      setBuyError(err instanceof Error ? err.message : "Could not start checkout.");
    } finally {
      setBuying(null);
    }
  }

  // Intervals that at least one (public or custom) plan actually prices.
  const available = useMemo(
    () => (data ? availableIntervals([...data.publicPlans, ...data.customPlans]) : []),
    [data],
  );

  // Default to monthly when offered (matches the web app's pricing toggle); only
  // set once data arrives so we don't reset a selection the user has made.
  useEffect(() => {
    if (available.length && selected === null) {
      setSelected(available.includes("monthly") ? "monthly" : defaultInterval(available));
    }
  }, [available, selected]);

  if (!data) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton" style={{ height: "120px" }} />
        <div className="skeleton" style={{ height: "200px", marginTop: "16px" }} />
      </div>
    );
  }

  const cur = data.current;
  // Only owners + admins can change the plan or buy credits; members are view-only.
  const isManager = data.viewerRole === "owner" || data.viewerRole === "admin";
  const interval: PlanInterval =
    selected ?? (available.includes("monthly") || !available.length ? "monthly" : defaultInterval(available));

  async function startCheckout(plan: PlanRecord) {
    if (!orgId) return;
    setCheckoutLoading(plan.id);
    setError(null);
    trackPlanUpgradeStart(cur?.kind ?? "free", plan.kind);
    try {
      const res = await createBillingCheckout(orgId, plan.id, interval);
      if (res.url) {
        window.location.href = res.url; // new subscription → hosted checkout
        return;
      }
      // Existing subscription was changed in place (no second subscription).
      // Confirm what happened, then go to Billing to see it.
      if (res.changed?.effectiveAt === "immediately") {
        toast(`You're now on ${plan.name}. A prorated amount for the rest of this period was charged today.`, true);
      } else if (res.changed) {
        const when = res.changed.endsAt
          ? new Date(res.changed.endsAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
          : "the end of your billing period";
        toast(`Downgrade to ${plan.name} scheduled for ${when}. You keep your current plan until then.`, true);
      }
      navigate(`/dashboard/${orgId}/billing`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change the plan.");
    } finally {
      setCheckoutLoading(null);
    }
  }

  // Render a single plan card (used for both public + custom plans).
  function PlanCard({ p }: { p: PlanRecord }) {
    const isCurrent = p.id === cur.planId;
    const isFreePlan = p.id === "free"; // the actual Hobby plan (the downgrade target)
    // A paid target below the current tier is a (scheduled) downgrade, not an
    // upgrade — label it honestly. Tier order comes from SELECTABLE_PLANS; if
    // either side is a custom/unknown plan (rank -1) we leave it as "Upgrade".
    const curRank = SELECTABLE_PLANS.indexOf(cur.planId as (typeof SELECTABLE_PLANS)[number]);
    const targetRank = SELECTABLE_PLANS.indexOf(p.id as (typeof SELECTABLE_PLANS)[number]);
    const isDowngrade = curRank >= 0 && targetRank >= 0 && targetRank < curRank;
    const priced = p.prices[interval] ?? 0;
    const lowest = lowestPrice(p);
    // Enterprise/custom "contact us" = no prices at all AND no checkout product ids.
    const noCheckout = !hasAnyPrice(p) && Object.keys(p.dodoProductIds ?? {}).length === 0;
    const isLoadingThis = checkoutLoading === p.id;
    const memberLimit = planMemberLimit(p);
    const hasSso = planHasSso(p);

    // Price display for the selected interval.
    let priceNode: ReactNode;
    if (isFreePlan) {
      priceNode = <>Free</>;
    } else if (priced > 0) {
      priceNode = (
        <>
          {money(priced)}
          <small style={{ fontSize: "13px", color: "var(--text-subtle)", fontWeight: 400, fontFamily: "var(--font-sans)" }}>/{SHORT[interval]}</small>
        </>
      );
    } else if (lowest) {
      // No price for the selected interval — show the lowest available instead.
      priceNode = (
        <>
          {money(lowest.cents)}
          <small style={{ fontSize: "13px", color: "var(--text-subtle)", fontWeight: 400, fontFamily: "var(--font-sans)" }}>/{SHORT[lowest.interval]}</small>
        </>
      );
    } else {
      // Enterprise/custom with no list price — always a bespoke quote.
      priceNode = <>Custom</>;
    }

    return (
      <div
        key={p.id}
        style={{
          border: `1px solid ${isCurrent ? "var(--accent-line)" : "var(--border)"}`,
          borderRadius: "var(--radius)",
          padding: "20px",
          background: isCurrent
            ? "linear-gradient(180deg, var(--accent-soft), transparent 60%)"
            : "var(--surface)",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          position: "relative",
          transition: "border-color .15s var(--ease)",
        }}
      >
        {isCurrent && (
          <span className="badge badge-accent" style={{ position: "absolute", top: "14px", right: "14px" }}>Current</span>
        )}
        {p.kind === "custom" && !isCurrent && (
          <span className="badge badge-warn" style={{ position: "absolute", top: "14px", right: "14px" }}>Custom</span>
        )}
        <div style={{ fontFamily: "var(--font-display)", fontSize: "16px", fontWeight: 600 }}>{p.name}</div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: "30px", fontWeight: 600, letterSpacing: "-.02em" }}>
          {priceNode}
        </div>
        <ul style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px" }}>
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {isUnlimited(p.monthlyRequests) ? "Unlimited requests / mo" : `${fmt(p.monthlyRequests!)} requests / mo`}
          </li>
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {memberLimit === null ? "Unlimited members" : `${memberLimit} members`}
          </li>
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {p.aiCreditLimit === null ? "Custom AI credits" : `${fmt(p.aiCreditLimit)} AI credits / mo`}
          </li>
          {rateLimitText(p) ? (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              {rateLimitText(p)} rate limit
            </li>
          ) : p.id === "enterprise" ? (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              Custom rate limit
            </li>
          ) : null}
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {"MCP access & PPP adjustment"}
          </li>
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {(() => {
              const days = (PLAN_LIMITS as Record<string, { historicalDays?: number | null }>)[p.id]?.historicalDays ?? null;
              return days != null ? `Last ${days} days of historical FX data` : "75+ years of historical FX data";
            })()}
          </li>
          {p.historyApiEnabled && (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              {"Analytics suite"}
            </li>
          )}
          {p.historyApiEnabled && (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              {"Bring your own AI keys (BYOK)"}
            </li>
          )}
          {hasSso && (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              Single Sign-On (SSO)
            </li>
          )}
          {p.auditLogsEnabled && (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              Audit logs
            </li>
          )}
          <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <CheckSVG />
            {isFreePlan ? "Community support" : p.prioritySupportEnabled ? "Priority support" : "Email support"}
          </li>
          {overageRateText(p.overageCents, p.overageUnit) && (
            <li style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <CheckSVG />
              Overage {overageRateText(p.overageCents, p.overageUnit)}
            </li>
          )}
        </ul>
        {isCurrent ? (
          <button className="btn" disabled style={{ width: "100%", justifyContent: "center", opacity: 0.6 }}>
            Current plan
          </button>
        ) : !isManager ? (
          <button className="btn" disabled title="Only owners and admins can change the plan" style={{ width: "100%", justifyContent: "center", opacity: 0.6 }}>
            Owners &amp; admins only
          </button>
        ) : isFreePlan ? (
          <Link
            className="btn"
            to={`/dashboard/${orgId}/billing`}
            style={{ width: "100%", justifyContent: "center" }}
          >
            Downgrade
          </Link>
        ) : noCheckout ? (
          <button
            className="btn"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={() => window.open("mailto:hello@currency-core.com?subject=Plan inquiry")}
          >
            Contact us
          </button>
        ) : (
          <button
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center" }}
            disabled={isLoadingThis}
            onClick={() => {
              // Paid org → in-place change (immediate charge or scheduled
              // downgrade): confirm first. Free org → fresh hosted checkout,
              // where Dodo shows + confirms the charge.
              if (cur.planId === "free") { void startCheckout(p); return; }
              setConfirm({ plan: p, isDowngrade });
              // For an upgrade (immediate charge), fetch Dodo's exact proration
              // so the modal can show "charged today". Downgrades cost nothing now.
              if (!isDowngrade && orgId) {
                setPreview("loading");
                previewPlanChange(orgId, p.id, interval).then(setPreview).catch(() => setPreview(null));
              } else {
                setPreview(null);
              }
            }}
          >
            {isLoadingThis ? "Redirecting…" : isDowngrade ? "Downgrade" : "Upgrade"}
          </button>
        )}
      </div>
    );
  }

  // Confirmation for a paid→paid change (built here so `confirm` is narrowed).
  let confirmModal: ReactNode = null;
  if (confirm) {
    const dn = confirm.isDowngrade;
    const pl = confirm.plan;
    const cents = pl.prices[interval] || 0;
    const recurring = cents ? `${money(cents)}/${SHORT[interval]}` : "its listed price";
    const close = () => { setConfirm(null); setPreview(null); };
    // Exact today's charge from Dodo's proration preview, when available.
    const today =
      preview && preview !== "loading" && preview.available && preview.amountCents != null
        ? `${money(preview.amountCents)}`
        : null;
    confirmModal = createPortal(
      <div
        className="modal-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div className="modal-box animate-fade-in">
          <h2>{dn ? "Downgrade to" : "Upgrade to"} {pl.name}?</h2>
          <p>
            {dn ? (
              <>
                Your plan switches to <strong>{pl.name}</strong> at the end of your current billing
                period. You keep your current plan until then, and <strong>nothing is charged today</strong>.
                You can reverse it any time before it takes effect.
              </>
            ) : today ? (
              <>
                You'll be charged <strong>{today}</strong> today (prorated for the rest of this billing
                period), then <strong>{recurring}</strong> at each renewal. The change takes effect immediately.
              </>
            ) : (
              <>
                A <strong>prorated amount</strong> for the rest of this billing period is charged{" "}
                <strong>today</strong>
                {preview === "loading" ? " (calculating the exact figure…)" : ""}, then{" "}
                <strong>{recurring}</strong> at each renewal. The change takes effect immediately.
              </>
            )}
          </p>
          <div className="modal-foot">
            <button className="btn btn-ghost" onClick={close}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => { close(); void startCheckout(pl); }}
            >
              {dn ? "Schedule downgrade" : "Confirm upgrade"}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="animate-fade-in">
      {confirmModal}
      <div className="page-head rise">
        <div>
          <h1>Plans</h1>
          <p className="sub">
            Your current plan and what's available.{" "}
            <a href="https://currency-core.com/pricing" target="_blank" rel="noopener noreferrer">
              See full plan comparison →
            </a>
          </p>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: "18px" }}>{error}</div>
      )}

      {/* Current plan */}
      <div className="card card-pad rise">
        <div className="row gap-sm">
          <strong style={{ fontSize: "15px" }}>{cur.label}</strong>
          <span className="badge badge-accent">Current plan</span>
          {cur.interval && <span className="badge badge-neutral">{intervalLabel(cur.interval)}</span>}
          {cur.kind === "custom" && <span className="badge badge-warn">Custom</span>}
        </div>
        <p className="muted" style={{ fontSize: "13px", marginTop: "8px" }}>
          {cur.effectiveLimit === null ? "Unlimited requests / month" : `${fmt(cur.effectiveLimit)} requests / month`}
          {cur.grant?.active && (
            <>
              {" "}(base {cur.monthlyLimit === null ? "∞" : fmt(cur.monthlyLimit)} <strong>+ {fmt(cur.grant.requests)} grant</strong>)
              {cur.grant.endPeriod ? ` through ${cur.grant.endPeriod}` : ""}
            </>
          )}
        </p>
        {overageRateText(cur.overageCents, cur.overageUnit) && (
          <p className="muted" style={{ fontSize: "12.5px", marginTop: "4px" }}>
            Over-usage billed at <strong>{overageRateText(cur.overageCents, cur.overageUnit)}</strong>.
          </p>
        )}
      </div>

      {/* Custom plan detail (only orgs on the plan's allow-list see these) */}
      {data.customPlans.map((cp) => (
        <div className="card mt rise" key={cp.id}>
          <div className="card-head">
            <h2>{cp.name}</h2>
            <span className="badge badge-warn">
              {cp.id === cur.planId ? "Your custom plan" : "Available to you"}
            </span>
          </div>
          <div className="card-pad">
            <p className="muted" style={{ fontSize: "13px", marginBottom: "10px" }}>
              {isUnlimited(cp.monthlyRequests) ? "Unlimited" : fmt(cp.monthlyRequests!)} requests / month
              {overageRateText(cp.overageCents, cp.overageUnit) && (
                <> · overage {overageRateText(cp.overageCents, cp.overageUnit)}</>
              )}
            </p>
            <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
              {INTERVALS.filter((i) => (cp.prices[i] ?? 0) > 0).map((i) => (
                <span key={i} className="badge badge-neutral">{money(cp.prices[i]!)} / {intervalLabel(i).toLowerCase()}</span>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Available plans + interval selector */}
      <div className="mt-lg rise">
        <div className="between" style={{ marginBottom: "14px" }}>
          <h2 style={{ fontSize: "17px" }}>Available plans</h2>
          {available.length > 1 && (
            <div className="segmented">
              {available.map((i) => (
                <button
                  key={i}
                  className={interval === i ? "active" : ""}
                  onClick={() => setSelected(i)}
                >
                  {intervalLabel(i)}
                  {i === "annual" && <span className="seg-save">2 months free</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-4">
          {data.customPlans.map((p) => <PlanCard key={p.id} p={p} />)}
          {data.publicPlans.map((p) => <PlanCard key={p.id} p={p} />)}
        </div>
      </div>

      {/* AI credit add-ons — one-time purchases (not a subscription). */}
      <div className="mt-lg rise" id="ai-credits" ref={aiCreditsRef} style={{ scrollMarginTop: "84px" }}>
        <div className="between" style={{ marginBottom: "14px" }}>
          <h2 style={{ fontSize: "17px" }}>AI credit add-ons</h2>
        </div>

        <div className="card card-pad" style={{ marginBottom: "16px" }}>
          <p className="muted" style={{ fontSize: "13px", margin: 0, lineHeight: 1.6 }}>
            <strong style={{ color: "var(--text)" }}>1 AI credit = 1 AI request.</strong> Top up your organization's AI credits.{" "}
            <strong style={{ color: "var(--text)" }}>These are one-time purchases, not a subscription.</strong> You're
            charged once, and <strong style={{ color: "var(--text)" }}>purchased credits never expire</strong>. They're
            used only after your plan's monthly AI allowance runs out, and carry over across billing cycles, upgrades,
            and downgrades.{" "}
            <a
              href="https://currency-core.com/docs/ai-credits"
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--accent-text)" }}
            >
              Learn about AI credits →
            </a>
          </p>
        </div>

        {buyError && <div className="error-banner" style={{ marginBottom: "14px" }}>{buyError}</div>}

        {packages === null ? (
          <div className="skeleton" style={{ height: "150px" }} />
        ) : packages.length === 0 ? (
          <p className="muted" style={{ fontSize: "13px" }}>No credit packages are available right now.</p>
        ) : (
          <div className="grid grid-4">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: "20px",
                  background: "var(--surface)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontFamily: "var(--font-display)", fontSize: "26px", fontWeight: 600, letterSpacing: "-.02em" }}>
                  {fmt(pkg.credits)}
                </div>
                <div className="muted" style={{ fontSize: "12px", marginTop: "-4px" }}>credits</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: "18px", fontWeight: 600, marginTop: "2px" }}>
                  {money(pkg.priceCents)}
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: "100%", justifyContent: "center", marginTop: "12px" }}
                  disabled={buying !== null || !isManager}
                  title={!isManager ? "Only owners and admins can purchase credits" : undefined}
                  onClick={() => void buyCredits(pkg.id)}
                >
                  {buying === pkg.id ? "Redirecting…" : !isManager ? "Owners & admins only" : "Buy credits"}
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="muted" style={{ fontSize: "12px", marginTop: "12px" }}>
          Billed once via Dodo Payments. Credits land on your balance as soon as the payment completes.
        </p>
      </div>
    </div>
  );
}
